import AnswerData from '@ax2/common/model/answers-data/AnswerData';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import OptionsListAnswerData from '@ax2/common/model/answers-data/OptionsListAnswerData';
import QuestionType from '@ax2/common/model/QuestionType';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import OptionsGridAnswerData from '@ax2/common/model/answers-data/OptionsGridAnswerData';
import _ from 'lodash';
import {
  isArrayMinLength,
  isArrayNotEmpty,
  isArraysEquals,
  isArraySubset,
  isCartesianSubset,
  isIdInArray,
} from './validations';
import OptionData from '@ax2/common/model/answers-data/OptionData';
import OptionCellData from '@ax2/common/model/answers-data/OptionCellData';
import AnswerRowSchema from '@ax2/common/model/schemas/AnswerRowSchema';
import SingleOptionAnswerData from '@ax2/common/model/answers-data/SingleOptionAnswerData';
import TextAnswerData from '@ax2/common/model/answers-data/TextAnswerData';
import TextCellData from '@ax2/common/model/answers-data/TextCellData';
import TextsGridAnswerData from '@ax2/common/model/answers-data/TextsGridAnswerData';
import TextsListAnswerData from '@ax2/common/model/answers-data/TextsListAnswerData';
import TextData from '@ax2/common/model/answers-data/TextData';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import AnswerType from '@ax2/common/model/AnswerType';
import UUID from '@ax2/common/model/UUID';
import { GridRequiredStrategy } from '@ax2/common/model/schemas/GridRequiredOptions';
import RankingOptionAnswerData from '@ax2/common/model/answers-data/RankingOptionAnswerData';

export function getValidation(
  qSchema: QuestionSchema,
  isDesktop: boolean,
  data: AnswerData | undefined
): string | null {
  switch (qSchema.questionType) {
    case QuestionType.CheckboxList: {
      const tData = data ? (data as OptionsListAnswerData) : null;
      const selection: OptionData[] = tData ? tData.selected : [];

      const pdError = tData
        ? getParticipantDefinedValError(
            (qSchema as ListQuestionSchema).answerOptions,
            tData.participantDefinedOption,
            _.map(selection, s => s.id)
          )
        : null;
      if (pdError) {
        return pdError;
      } else {
        return isArrayNotEmpty(_.map(selection, s => s.id))
          ? null
          : 'At least one option is required';
      }
    }
    case QuestionType.RatingList:
    case QuestionType.RadioButtonGrid:
    case QuestionType.RadioButtonGridRow:
    case QuestionType.CheckboxGrid: {
      const tData = data ? (data as OptionsGridAnswerData) : null;
      const selection: OptionCellData[] = tData ? tData.selected : [];
      const rows: AnswerRowSchema[] = (qSchema as GridQuestionSchema).rows;

      if (qSchema.questionType !== QuestionType.RatingList) {
        const pdError = tData
          ? getParticipantDefinedValError(
              rows,
              tData.participantDefinedOption,
              _.map(selection, s => s.rowId)
            )
          : null;
        if (pdError) {
          return pdError;
        }
      }

      return getGridValError(
        qSchema as GridQuestionSchema,
        isDesktop,
        selection
      );
    }

    case QuestionType.RadioButtonList:
    case QuestionType.DropdownMenu: {
      const tData = data ? (data as SingleOptionAnswerData) : null;
      const id = tData ? tData.selectedId : null;

      const pdError =
        tData && id
          ? getParticipantDefinedValError(
              (qSchema as ListQuestionSchema).answerOptions,
              tData.participantDefinedOption,
              [id]
            )
          : null;
      if (pdError) {
        return pdError;
      } else {
        return id ? null : 'Answer is required';
      }
    }
    case QuestionType.OpenEndedText: {
      const text = data ? (data as TextAnswerData).text : null;
      return text ? null : 'Answer is required';
    }
    case QuestionType.OpenEndedNumeric: {
      const text = data ? (data as TextAnswerData).text : null;
      return text ? null : 'Answer is required';
    }
    case QuestionType.TextboxGridNumeric: {
      const tData = data ? (data as TextsGridAnswerData) : null;
      const selection: TextCellData[] = tData ? tData.selected : [];

      const rows: AnswerRowSchema[] = (qSchema as GridQuestionSchema).rows;

      const pdError = tData
        ? getParticipantDefinedValError(
            rows,
            tData.participantDefinedOption,
            _.map(selection, s => s.rowId)
          )
        : null;
      if (pdError) {
        return pdError;
      } else {
        return getGridValError(
          qSchema as GridQuestionSchema,
          isDesktop,
          selection
        );
      }
    }

    case QuestionType.TextboxListText:
    case QuestionType.TextboxListNumeric: {
      const requiredOptions = (qSchema as ListQuestionSchema).requiredOptions;
      const minAmountOpt = requiredOptions ? requiredOptions.minAmount : null;
      const minAmount = minAmountOpt ? minAmountOpt : 1;

      const selection: TextData[] = data
        ? (data as TextsListAnswerData).selected
        : [];
      return isArrayMinLength(_.map(selection, s => s.id), minAmount)
        ? null
        : 'You have to fill all fields';
    }
    case QuestionType.RankingList: {
      const minAmount = qSchema.answerOptions.length;
      const selection: OptionData[] = data
        ? (data as RankingOptionAnswerData).selected
        : [];
      return isArrayMinLength(
        _.map(_.filter(selection, s => !!s), s => s.id),
        minAmount
      )
        ? null
        : 'You have to fill all fields';
    }
    case QuestionType.ContinuousSumList: {
      const selection: TextData[] = data
        ? (data as TextsListAnswerData).selected
        : [];
      const length = qSchema.answerOptions.length;
      if (!isArrayMinLength(_.map(selection, s => s.id), length)) {
        return 'You have to fill all fields';
      }
      if (
        _.sum(selection.map(o => Number.parseInt(o.text || '0', 10))) !== 100
      ) {
        return 'Sum must be exactly 100%';
      }
      return null;
    }
    default:
      return null;
  }
}

function getParticipantDefinedValError(
  aSchemas: { id: UUID; typ: AnswerType }[],
  participantDefinedOption: string | null,
  data: UUID[] | null
): string | null {
  const pSchema = _.find(
    aSchemas,
    f => f.typ === AnswerType.ParticipantDefined
  );
  const isFullfilled = pSchema && data ? isIdInArray(pSchema.id, data) : false;

  if (!participantDefinedOption && isFullfilled) {
    return '\'Other\' option text is required';
  } else {
    return null;
  }
}

function getGridValError(
  schema: GridQuestionSchema,
  isDesktop: boolean,
  data: { rowId: UUID; columnId: UUID }[] | null
): string | null {
  if (schema.isRequired && !schema.requiredOptions) {
    throw Error(
      `Failed to create validation. RequiredOptions wasn't specified for grid schema (${schema.questionType}) with required flag`
    );
  } else if (schema.isRequired && schema.requiredOptions) {
    switch (schema.requiredOptions.strategy) {
      case GridRequiredStrategy.All:
        return isCartesianSubset(
          _.map(data, d => {
            return { id1: d.rowId, id2: d.columnId };
          }),
          _.map(schema.rows, r => r.id),
          _.map(schema.columns, c => c.id)
        )
          ? null
          : 'All answers are required';
      case GridRequiredStrategy.OnePerColumn:
        return isArraysEquals(
          _.map(schema.columns, c => c.id),
          _.map(data, d => d.columnId)
        )
          ? null
          : isDesktop
          ? 'One answer per column is required'
          : 'One answer per section is required';
      case GridRequiredStrategy.OnePerRow:
        return isArraysEquals(
          _.map(schema.rows, r => r.id),
          _.map(data, d => d.rowId)
        )
          ? null
          : isDesktop
          ? 'One answer per row is required'
          : 'One answer per section is required';
      case GridRequiredStrategy.AtLeastOnePerColumn:
        return isArraySubset(
          _.map(schema.columns, c => c.id),
          _.map(data, d => d.columnId)
        )
          ? null
          : isDesktop
          ? 'At least one answer per column is required'
          : 'At least one answer per section is required';
      case GridRequiredStrategy.AtLeastOnePerRow:
        return isArraySubset(
          _.map(schema.rows, r => r.id),
          _.map(data, d => d.rowId)
        )
          ? null
          : schema.questionType === QuestionType.RatingList
          ? 'One answer per row is required' // We have different error text for Rating List
          : isDesktop
          ? 'At least one answer per row is required'
          : 'At least one answer section is required';
      default:
        throw Error('Unexpected GridRequiredStrategy validation type');
    }
  } else {
    return null;
  }
}
